<template>
  <div class="app-container">
    <FaqPanel
      page="transactions"
      :top-offset="50"
      :max-width="350"
      :backdrop="true"
    />
    <el-container>
      <el-main class="wrapper">
        <div class="tab-container">
          <el-tabs v-model="activeTab">
            <el-tab-pane
              key="income"
              :label="`Income history: $${sumIncome}`"
              name="income"
            >
              <TransactionsTable
                :items="itemsIncome"
                :total="totalIncome"
                :type="TransactionType.Added"
                :with-invoice="true"
                @update="update"
              />
            </el-tab-pane>
            <el-tab-pane
              key="spends"
              :label="`Withdrawn history: $${sumSpend}`"
              name="spends"
            >
              <TransactionsTable
                :items="itemsSpend"
                :total="totalSpend"
                :type="TransactionType.Spends"
                @update="update"
              />
            </el-tab-pane>
            <el-tab-pane
              key="invoices"
              label="Requested invoices"
              name="invoices"
            >
              <InvoicesTable
                :items="invoices"
                :total="totalInvoices"
                @update="updateInvoices"
              />
            </el-tab-pane>
          </el-tabs>
        </div>
      </el-main>
    </el-container>
  </div>
</template>

<script lang="ts">
import { computed, ref, defineComponent, onMounted } from 'vue'
import useRouter from '@/hooks/useRouter'
import { ElMessage } from 'element-plus'

import TransactionsTable from './components/TransactionsTable.vue'
import InvoicesTable from './components/InvoicesTable.vue'
import FaqPanel from '@/components/FaqPanel/Index.vue'

import { TransactionType } from '@/api/types'
import { ITransaction, ITransactionsPayload } from '@/api/transactions'
import { IReplenishment } from '@/interfaces/replenishments'

import { useTransactionsStore } from '@/store/modules/transactions'
import { useReplenishmentsStore } from '@/store/modules/replenishments'
import { useNotificationStore } from '@/store/modules/notification'

import { itemsIncome, totalIncome } from './shared'

export default defineComponent({
  name: 'Transactions',
  components: {
    TransactionsTable,
    InvoicesTable,
    FaqPanel
  },
  setup(props, context) {
    const transactionsStore = useTransactionsStore()
    const replenishmentsStore = useReplenishmentsStore()
    const notificationStore = useNotificationStore()
    const loading = ref(true)
    const activeTab = ref('income')

    const { route } = useRouter()

    const showMessage = (success: boolean) => {
      const successText = 'Thank you for payment!'
      const failureText =
        'Payment declined. No funds were charged. Please try again or contact support'
      ElMessage({
        message: success ? successText : failureText,
        type: success ? 'success' : 'error',
        showClose: true,
        duration: 5000
      })
    }

    notificationStore.ShowNotification()

    const successMessage = Object.prototype.hasOwnProperty.call(
      route.value.query,
      'success'
    )
    const failureMessage = Object.prototype.hasOwnProperty.call(
      route.value.query,
      'failure'
    )
    const invoicesTabVisible = Object.prototype.hasOwnProperty.call(
      route.value.query,
      'invoices'
    )

    if (successMessage || failureMessage) {
      showMessage(successMessage)
    }

    if (invoicesTabVisible) {
      activeTab.value = 'invoices'
    }

    const update = ({
      typeId,
      limit = 20,
      newPage = 1
    }: {
      typeId: TransactionType
      limit?: number
      newPage?: number
    }) => {
      transactionsStore.SetCurrentPage(newPage)
      transactionsStore.SetFilters({ type_id: typeId })
      transactionsStore.FetchItems()
    }

    const updateInvoices = ({
      limit = 1000,
      newPage = 1
    }: {
      limit?: number
      newPage?: number
    }) => {
      replenishmentsStore.SetCurrentPage(newPage)
      replenishmentsStore.SetLimit(limit)
      replenishmentsStore.SetFilters({})
      replenishmentsStore.FetchItems()
    }

    const itemsSpend = computed(() => {
      return transactionsStore.currentItems[TransactionType.Spends] || []
    })

    const totalSpend = computed(() => {
      return transactionsStore.total[TransactionType.Spends] || 0
    })

    const sumSpend = computed(() => {
      return transactionsStore.sum[TransactionType.Spends] || 0
    })

    const sumIncome = computed(() => {
      return transactionsStore.sum[TransactionType.Added] || 0
    })

    const invoices = computed(() => {
      return replenishmentsStore.currentItems.filter(
        (p: IReplenishment) => p.attributes.with_invoice
      )
    })

    const totalInvoices = computed(() => {
      return replenishmentsStore.currentItems.filter(
        (p: IReplenishment) => p.attributes.with_invoice
      ).length
    })

    const rejectedInvoices = computed(() => {
      return replenishmentsStore.currentItems.filter(
        (p: IReplenishment) => p.attributes.status === 'rejected'
      )
    })

    onMounted(() => {
      update({ typeId: TransactionType.Added })
      update({ typeId: TransactionType.Spends })
      updateInvoices({})
    })

    return {
      loading,
      activeTab,
      itemsSpend,
      totalSpend,
      sumSpend,
      itemsIncome,
      totalIncome,
      sumIncome,
      TransactionType,
      update,
      invoices,
      updateInvoices,
      totalInvoices
    }
  }
})
</script>
